/**
 * purchaseDataSource.ts — WatermelonDB → BooksPurchase bridge for GSTR-2B
 * reconciliation. Loads the taxpayer's recorded inward supplies (received POs)
 * for a period, keyed by the supplier's bill number + GSTIN so they can be
 * matched against the portal's GSTR-2B.
 */

import { Q } from '@nozbe/watermelondb';
import { database } from '@/db';
import { PurchaseOrder, PoLineItem, Supplier } from '@/db/models/Inventory';
import type { BooksPurchase } from './gstr2b';
import type { TaxHeads } from './gst3b';

const CHUNK = 500;

async function fetchInChunks<T>(ids: string[], run: (batch: string[]) => Promise<T[]>): Promise<T[]> {
  const out: T[] = [];
  for (let i = 0; i < ids.length; i += CHUNK) {
    out.push(...(await run(ids.slice(i, i + CHUNK))));
  }
  return out;
}

/** Load received/partial purchases for [from, to] as BooksPurchase rows. */
export async function loadBooksPurchases(storeId: string, from: Date, to: Date): Promise<BooksPurchase[]> {
  const pos = await database.get<PurchaseOrder>('purchase_orders').query(
    Q.where('store_id', storeId),
    Q.where('is_deleted', false),
    Q.where('status', Q.oneOf(['RECEIVED', 'PARTIAL'])),
    Q.where('created_at', Q.gte(from.getTime())),
    Q.where('created_at', Q.lte(to.getTime())),
  ).fetch();

  const poIds = pos.map((p) => p.id);
  const lines = await fetchInChunks(poIds, (batch) =>
    database.get<PoLineItem>('po_line_items').query(
      Q.where('purchase_order_id', Q.oneOf(batch)),
      Q.where('is_deleted', false),
    ).fetch(),
  );
  const linesByPo = new Map<string, PoLineItem[]>();
  for (const li of lines) {
    const arr = linesByPo.get(li.purchaseOrderId) ?? [];
    arr.push(li);
    linesByPo.set(li.purchaseOrderId, arr);
  }

  const supIds = [...new Set(pos.map((p) => p.supplierId).filter(Boolean))];
  const suppliers = await fetchInChunks(supIds, (batch) =>
    database.get<Supplier>('suppliers').query(Q.where('id', Q.oneOf(batch))).fetch(),
  );
  const supMap = new Map<string, Supplier>();
  for (const s of suppliers) supMap.set(s.id, s);

  return pos.map((po) => {
    const sup = supMap.get(po.supplierId);
    const poLines = linesByPo.get(po.id) ?? [];
    const cgst = poLines.reduce((s, l) => s + l.cgstAmount, 0);
    const sgst = poLines.reduce((s, l) => s + l.sgstAmount, 0);
    const igst = poLines.reduce((s, l) => s + l.igstAmount, 0);
    const taxable = poLines.reduce((s, l) => s + l.unitPrice * l.quantity, 0);
    return {
      supplierGstin: sup?.gstin ?? null,
      supplierName: sup?.name?.trim() || 'Supplier',
      // The supplier's bill number is what GSTR-2B reports; fall back to PO no.
      invoiceNumber: po.supplierInvoiceNo?.trim() || po.poNumber,
      date: po.supplierInvoiceDate ?? po.createdAt,
      taxable,
      igst,
      cgst,
      sgst,
      cess: 0,
      totalTax: cgst + sgst + igst,
      value: po.totalAmount,
      poNumber: po.poNumber,
    };
  });
}

/** Sum the input tax credit across a set of purchases (GSTR-3B Table 4). */
export function sumPurchaseItc(purchases: BooksPurchase[]): TaxHeads {
  const round2 = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100;
  const acc = purchases.reduce(
    (s, p) => ({ igst: s.igst + p.igst, cgst: s.cgst + p.cgst, sgst: s.sgst + p.sgst, cess: s.cess + p.cess }),
    { igst: 0, cgst: 0, sgst: 0, cess: 0 },
  );
  return { igst: round2(acc.igst), cgst: round2(acc.cgst), sgst: round2(acc.sgst), cess: round2(acc.cess) };
}

/**
 * Input tax credit available for a period — the GSTR-3B Table 4 figure, summed
 * from received purchases. Period ITC only; an opening credit-ledger balance is
 * not tracked. Blocked-ITC (Section 17(5)) is not yet excluded — see the ITC
 * Register plan item.
 */
export async function loadPeriodItc(storeId: string, from: Date, to: Date): Promise<TaxHeads> {
  return sumPurchaseItc(await loadBooksPurchases(storeId, from, to));
}
